import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import { Box, FormControl, FormLabel } from '@mui/material';
import React from 'react';
import { RadioItemPicker } from '../RadioItemPicker';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { pickerIcons } from '../icons/icons';
import { type } from './types';
const WidgetFormIconPicker = (props) => {
    var _a, _b, _c;
    const value = props.getDataStringOrUndefined();
    const error = getVisibleError(props);
    const customIcons = (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.iconPickerIcons;
    const formLabelSuffix = getFormLabelSuffix(props);
    const iconItems = React.useMemo(() => {
        return Object.entries(customIcons !== null && customIcons !== void 0 ? customIcons : pickerIcons).map(([key, { icon, 'aria-label': ariaLabel, tooltip }]) => ({
            value: key,
            element: React.createElement(IconWrapper, { icon: icon }),
            'aria-label': ariaLabel,
            tooltip: tooltip,
        }));
    }, [customIcons]);
    return (React.createElement(FormControl, { fullWidth: true, required: (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required, error: !!error },
        props.config.label && (React.createElement(FormLabel, { sx: { mb: 1 } }, formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label)),
        React.createElement(RadioItemPicker, { items: iconItems, selectedItemValue: value, onSelectItem: (item) => {
                props.setDataStringOrUndefined(item === null || item === void 0 ? void 0 : item.value);
                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
            }, autoFocus: (_c = (!props.editMode && props.config.autoFocus)) !== null && _c !== void 0 ? _c : false }),
        React.createElement(FormHelperErrorDescription, { error: error, description: props.config.description })));
};
const WidgetFormIconPickerEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Icon: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            labelOptional: true,
            appendRowIndexToLabel: true,
            description: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.radio',
                    key: 'icon',
                    label: 'Icon',
                    style: {
                        inline: true,
                    },
                    event: {
                        onChange: (value, handler) => {
                            const defaultValue = handler.up().down('defaultValue');
                            if (defaultValue) {
                                defaultValue.setData(undefined);
                                defaultValue.update();
                            }
                        },
                    },
                },
                {
                    type: 'form.checkbox',
                    key: 'setDefaultValue',
                    label: 'Default Value',
                },
                {
                    type: 'form.iconPicker',
                    key: 'defaultValue',
                    validate: {
                        required: true,
                    },
                    hidden: {
                        when: '^setDefaultValue',
                        dataNotEq: true,
                    },
                    clearDataOnHide: true,
                },
            ],
            onLoad: (data) => {
                if (data.defaultValue) {
                    data.setDefaultValue = true;
                }
                return data;
            },
            onSubmit: (data) => {
                delete data.setDefaultValue;
                return data;
            },
        }), buildValidateTab(props, {
            required: true,
        }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormIconPicker, Object.assign({}, props))));
};
export const widgetFormIconPicker = {
    type,
    init: (props) => {
        if (props.getDataStringOrUndefined() == null && props.config.defaultValue != null) {
            props.setDataStringOrUndefined(props.config.defaultValue);
        }
    },
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataStringOrUndefined();
        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) && value == null) {
            return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a;
        const value = props.getDataStringOrUndefined();
        return getLabelAndValuePairs(joinLabels(labelPrefix, (_a = props.config.label) !== null && _a !== void 0 ? _a : 'Icon Picker'), value);
    },
    component: WidgetFormIconPicker,
    icon: React.createElement(CenterFocusStrongOutlinedIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormIconPickerEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'iconPicker', label: 'Icon Picker' }),
};
const IconWrapper = (props) => {
    return (React.createElement(Box, { sx: { height: 24 } },
        React.createElement(props.icon, { sx: { p: 0.25, color: themeColors.icon.main } })));
};
